import { useState, useCallback } from "react";
import { useNavigate, Link } from "react-router-dom";
import { Volume2, Bell, User, Settings as SettingsIcon, Zap, Sparkles, GitBranch, FileText, ShieldCheck, Users as UsersIcon, Calendar as CalendarIcon } from "lucide-react";
import { MainLayout } from "@/components/layout";
import { PageHeader } from "@/components/shared";
import { useAuth } from "@/hooks/useAuth";
import { cn } from "@/lib/utils";
import VoiceSelector from "@/components/settings/VoiceSelector";
import GreetingSettings from "@/components/settings/GreetingSettings";
import AutomationsSettings from "@/components/settings/AutomationsSettings";
import VersionSection from "@/components/settings/VersionSection";
import NotificationBell from "@/components/shared/NotificationBell";  // FaseC C1

// Tabs: Voz, Automações (SMART-AUTOMATIONS), Notificações (placeholder),
// Perfil, Versão (VERSION-INDICATOR — front/back sync check).
const TABS = [
  { id: "voice",         label: "Voz",           icon: Volume2 },
  { id: "automations",   label: "Automações",    icon: Zap },
  { id: "notifications", label: "Notificações",  icon: Bell },
  { id: "profile",       label: "Perfil",        icon: User },
  { id: "version",       label: "Versão",        icon: GitBranch },
];

export default function SettingsPage() {
  const navigate = useNavigate();
  const { user, token } = useAuth();
  const userId = user?.id || "default_user";
  const [tab, setTab] = useState("voice");

  // Sidebar é compartilhada com o dashboard: se clicarem em outra seção,
  // voltamos pra / passando a intenção via URL hash (NectarDashboard lê e aplica).
  const handleSectionChange = useCallback((id) => {
    if (id === "settings") return; // já estamos aqui
    navigate(`/#${id}`);
  }, [navigate]);

  return (
    <MainLayout
      activeSection="settings"
      onSectionChange={handleSectionChange}
      showContextPanel={false}
      confirmations={[]}
      messages={[]}
    >
      <div className="p-6 md:p-8 max-w-5xl mx-auto" data-testid="settings-page">
        <PageHeader
          icon={SettingsIcon}
          title="Configurações"
          subtitle="Ajuste a voz do Nexo, notificações e preferências pessoais."
          actions={<NotificationBell />}
        />

        {/* Tabs */}
        <div className="flex items-center gap-1 mb-6 border-b border-subtle">
          {TABS.map((t) => {
            const Icon = t.icon;
            const isActive = tab === t.id;
            return (
              <button
                key={t.id}
                type="button"
                onClick={() => setTab(t.id)}
                aria-label={t.label}
                className={cn(
                  "flex items-center gap-2 px-3 sm:px-4 py-3 text-sm border-b-2 -mb-[2px] transition-colors duration-normal ease-smooth",
                  isActive
                    ? "text-brand-cyan border-brand-cyan"
                    : "text-foreground-muted border-transparent hover:text-foreground",
                )}
                data-testid={`settings-tab-${t.id}`}
              >
                <Icon className="w-4 h-4" />
                {/* FaseC C4: labels só a partir de sm (≥640px). Em iPhone SE
                    (375px) e iPhone 13 (390px) cabem 5 ícones em 1 linha;
                    aria-label preserva acessibilidade pra screen readers. */}
                <span className="hidden sm:inline">{t.label}</span>
              </button>
            );
          })}
        </div>

        {/* Content */}
        {tab === "voice" && (
          <section className="space-y-10">
            <div>
              <h2 className="text-h2 text-foreground mb-1">Voz do Nexo</h2>
              <p className="text-caption text-foreground-subtle mb-5">
                Escolha como o Nexo soa. O Nexo usa OpenAI TTS — vozes multilíngue, PT-BR natural.
              </p>
              <VoiceSelector userId={userId} />
            </div>

            <div className="border-t border-subtle pt-8">
              <h3 className="text-h3 text-foreground mb-1">Ativação e saudação</h3>
              <p className="text-caption text-foreground-subtle mb-5">
                Personalize como o Nexo te cumprimenta ao ligar o microfone.
              </p>
              <GreetingSettings userId={userId} authToken={token} />
            </div>
          </section>
        )}

        {tab === "automations" && (
          <AutomationsSettings />
        )}

        {tab === "notifications" && (
          <section className="p-8 bg-surface-card border border-subtle rounded-xl text-center">
            <p className="text-body text-foreground-muted">
              Configurações de notificação em breve.
            </p>
          </section>
        )}

        {tab === "profile" && (
          <section className="space-y-4">
            <div className="p-8 bg-surface-card border border-subtle rounded-xl">
              <h3 className="text-h3 text-foreground mb-2">Perfil</h3>
              <p className="text-caption text-foreground-muted">Nome: {user?.name || "—"}</p>
              <p className="text-caption text-foreground-muted">Email: {user?.email || "—"}</p>
            </div>
            {/* calendar-multiconta-1.3 — gerenciamento de conexões Google. */}
            <Link
              to="/configuracoes/conexoes"
              className="flex items-center justify-between p-5 rounded-xl bg-surface-card border border-subtle hover:border-brand-cyan/40 transition-colors group"
              data-testid="settings-link-conexoes"
            >
              <div className="flex items-center gap-3">
                <CalendarIcon className="w-5 h-5 text-brand-cyan" />
                <div>
                  <p className="text-body text-foreground">Gerenciar conexões</p>
                  <p className="text-caption text-foreground-muted">
                    Agendas Google ligadas ao Nexo
                  </p>
                </div>
              </div>
              <span className="text-caption text-foreground-muted group-hover:text-brand-cyan">
                →
              </span>
            </Link>

            {/* BILLING-S1B — link pra /planos.  Em S2 vira card completo
                de billing com plano atual + usage + portal. */}
            <Link
              to="/planos"
              className="flex items-center justify-between p-5 rounded-xl bg-surface-card border border-subtle hover:border-brand-cyan/40 transition-colors group"
              data-testid="settings-link-planos"
            >
              <div className="flex items-center gap-3">
                <Sparkles className="w-5 h-5 text-brand-cyan" />
                <div>
                  <p className="text-body text-foreground">Ver planos</p>
                  <p className="text-caption text-foreground-muted">
                    Conheça as opções de assinatura
                  </p>
                </div>
              </div>
              <span className="text-caption text-foreground-muted group-hover:text-brand-cyan">
                →
              </span>
            </Link>

            {/* D1 — atalho admin pra lista de users do beta. Só aparece
                pra admin (mesmo flag que protege a rota /admin/usuarios). */}
            {user?.is_admin && (
              <Link
                to="/admin/usuarios"
                className="flex items-center justify-between p-5 rounded-xl bg-surface-card border border-subtle hover:border-brand-cyan/40 transition-colors group"
                data-testid="settings-link-admin-users"
              >
                <div className="flex items-center gap-3">
                  <UsersIcon className="w-5 h-5 text-brand-cyan" />
                  <div>
                    <p className="text-body text-foreground">Beta · Usuários</p>
                    <p className="text-caption text-foreground-muted">
                      Quem se cadastrou e quem está testando
                    </p>
                  </div>
                </div>
                <span className="text-caption text-foreground-muted group-hover:text-brand-cyan">
                  →
                </span>
              </Link>
            )}

            {/* R4 — Documentos legais acessíveis ao usuário logado. */}
            <Link
              to="/privacidade"
              className="flex items-center justify-between p-5 rounded-xl bg-surface-card border border-subtle hover:border-brand-cyan/40 transition-colors group"
              data-testid="settings-link-privacidade"
            >
              <div className="flex items-center gap-3">
                <ShieldCheck className="w-5 h-5 text-brand-cyan" />
                <div>
                  <p className="text-body text-foreground">Política de Privacidade</p>
                  <p className="text-caption text-foreground-muted">
                    Como tratamos seus dados pessoais (LGPD)
                  </p>
                </div>
              </div>
              <span className="text-caption text-foreground-muted group-hover:text-brand-cyan">
                →
              </span>
            </Link>
            <Link
              to="/termos"
              className="flex items-center justify-between p-5 rounded-xl bg-surface-card border border-subtle hover:border-brand-cyan/40 transition-colors group"
              data-testid="settings-link-termos"
            >
              <div className="flex items-center gap-3">
                <FileText className="w-5 h-5 text-brand-cyan" />
                <div>
                  <p className="text-body text-foreground">Termos de Uso</p>
                  <p className="text-caption text-foreground-muted">
                    Condições de uso do Nexo
                  </p>
                </div>
              </div>
              <span className="text-caption text-foreground-muted group-hover:text-brand-cyan">
                →
              </span>
            </Link>
          </section>
        )}

        {tab === "version" && (
          <VersionSection />
        )}
      </div>
    </MainLayout>
  );
}
